import { Injectable, Inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { APIClientService } from '@registration/services/api-client.service';

import { APP_CONFIG } from '../resources/config';
import { RIDP_ERROR_MAP } from '../resources/errors';
import { ERR_INTERPOLATE } from '../resources/errors';

const CREATE_USER_SUCCESS = 'CREATE-USER-SUCCESS';
const CREATE_USER_FAIL = 'CREATE-USER-FAIL';

// @ts-ignore
require('./jsclib');
declare const adx: any;

/**
 * NOTE: There are 4 possible types of responses:
 * 1) HARP errors will return a simple object with an errorCode
 * 2) Experian happy path will return experianResponse.experianDecision === 'CONTINUE'
 * 3) Experian errors will return experianResponse.experianDecision !== 'CONTINUE'
 *    - handle the STOP error code
 *    - for all other errors, it returns the same message
 * 4) Manual proofing will return an empty experian object
 */
@Injectable()
export class ExperianProofingService {
  constructor(
    private apiClient: APIClientService,
    @Inject(APP_CONFIG) private config: any,
  ) {}

  public errorMessage: string;
  public experianReferenceId: string;

  private hdimBehaviorSubject = new BehaviorSubject('');
  hdim = this.hdimBehaviorSubject.asObservable();
  private hdimLoadedBehaviorSubject = new BehaviorSubject(false);
  hdimLoaded = this.hdimLoadedBehaviorSubject.asObservable();
  private jscBehaviorSubject = new BehaviorSubject('');
  jsc = this.jscBehaviorSubject.asObservable();

  async setHdimData(data) {
    this.hdimBehaviorSubject.next(data);
  }

  async setJscData() {
    const jscData = await adx.validate();
    this.jscBehaviorSubject.next(jscData);
  }

  async initiateHdim() {
    await adx.initiate(this.setJscData(), [
      'https://globalsiteanalytics.com/resource/resource.png',
      'https://globalsiteanalytics.com/service/hdim',
      'user_prefs2',
      null,
      null,
      null,
      hdimData => this.setHdimData(hdimData),
    ]);
    this.hdimLoadedBehaviorSubject.next(true);
  }

  async proofUser(payload) {
    try {
      this.reset();
      const isReproof = payload?.reproofingFlow;

      const url = isReproof
        ? `${this.config.BASE_EXPERIAN_URL}/retryRemoteProofing`
        : `${this.config.BASE_EXPERIAN_URL}/verfiyUser`;
      const { experianResponse, errorDetails, errorCode, errorSummary } =
        await this.apiClient.doPost(url, payload).toPromise();

      // harp error: stop the flow at Step 2
      if (errorCode) {
        return {
          responseStatus: CREATE_USER_FAIL,
          errorCode,
          errorMessage: errorSummary,
        };
      }

      // happy path
      if (
        experianResponse.experianDecision === 'CONTINUE' ||
        !Object.keys(experianResponse).length
      ) {
        return { responseStatus: CREATE_USER_SUCCESS };
      }

      const props = {
        experianReferenceId: `<strong>${
          experianResponse?.experianTransactionInfo?.clientReferenceId ||
          payload.experianReferenceId
        }</strong>`,
        errorCode: errorDetails?.errorCode,
        originUrl: window.location.origin,
      };

      // handle STOP decision
      if (experianResponse.experianDecision === 'STOP') {
        this.errorMessage = ERR_INTERPOLATE(
          RIDP_ERROR_MAP.HARPEXPERIAN600,
          props,
        );
        return {
          responseStatus: CREATE_USER_FAIL,
          errorMessage: this.errorMessage,
        };
      }

      // experian error: stop the flow at Step 2
      const errorTemplate =
        RIDP_ERROR_MAP[props.errorCode] || RIDP_ERROR_MAP['HARPEXPERIAN500'];

      this.errorMessage = ERR_INTERPOLATE(errorTemplate, props);

      return {
        responseStatus: CREATE_USER_FAIL,
        errorMessage: this.errorMessage,
      };
    } catch (err) {
      return {
        responseStatus: CREATE_USER_FAIL,
        errorCode: err?.error?.errorCode || 'HARPEXPERIAN500',
        errorMessage: err?.error?.errorSummary || 'Failed to verify user.',
      };
    }
  }

  reset() {
    this.errorMessage = null;
  }
}
